import React from "react";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import { FaArrowRight } from "react-icons/fa";

export const Solutions = () => {
    const [solutionsRef, isSolutionsVisible] = useScrollAnimation({ threshold: 0.2 });
    const [gridRef, isGridVisible] = useScrollAnimation({ threshold: 0.15 });
    const [calculatorRef, isCalculatorVisible] = useScrollAnimation({ threshold: 0.3 });

    // Data for the cards
    const cardsData = [
        {
            title: "Fleet Owners",
            description: "We help you evaluate the transition to electric vehicles by analyzing routes, vehicle tasks, operational patterns, and charging needs, providing clear insights into feasibility, costs, and long-term benefits.",
            image: "/img/homepage/fleet-owner-bg.png",
            imageAlt: "fleet-owner",
            linkTo: "/solutions?solution=fleet",
        },
        {
            title: "Charging Station Developers",
            description: "We help you identify optimal locations and determine the right station sizes by analyzing projected demand, usage patterns, and grid limitations, ensuring efficient, cost-effective deployment and strong utilization from day one.",
            image: "/img/homepage/charging.png",
            imageAlt: "charging",
            linkTo: "/solutions?solution=charger",
        },
        {
            title: "Utilities",
            description: "We work with you to plan grid upgrades and optimize system operations in response to growing EV adoption, integrating strategies like load forecasting, demand response, and smart charging to maintain reliability and control costs.",
            image: "/img/homepage/utility.png",
            imageAlt: "utility",
            linkTo: "/solutions?solution=utilities",
        },
        {
            title: "Regulators",
            description: "We assist you in analyzing the impact of proposed policies, incentive programs, and EV adoption scenarios, offering data-driven insights that support effective, forward-thinking decision-making.",
            image: "/img/homepage/regulator.png",
            imageAlt: "regulator",
            linkTo: "/solutions?solution=regulators",
        }
    ];

    return (
        <div className="p-4 sm:px-6 lg:px-8 py-8 mt-12 space-y-16">
            {/* Solutions Grid Card */}
            <div 
                ref={gridRef}
                className={`bg-white rounded-[20px] p-12 lg:p-16 shadow-sm transition-all duration-1000 ease-out transform ${
                    isGridVisible 
                        ? 'opacity-100 translate-y-0' 
                        : 'opacity-0 translate-y-8'
                }`}
            >
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h1 className="text-3xl lg:text-4xl font-nunito font-bold text-black mb-6">
                            What can we do for you?
                        </h1>
                    </div>

                    {/* Grid of Cards */}
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                        {cardsData.map((card, index) => (
                            <div 
                                key={index} 
                                className={`bg-gray-50 rounded-xl p-8 h-full transition-all duration-150 ease-in-out transform hover:shadow-xl hover:-translate-y-2 hover:scale-105 cursor-pointer ${
                                    isGridVisible 
                                        ? 'opacity-100 translate-y-0' 
                                        : 'opacity-0 translate-y-8'
                                }`}
                                style={{
                                    transitionDelay: isGridVisible ? `${index * 200}ms` : '0ms'
                                }}
                            >
                                <div className="flex flex-col h-full">
                                    <div className="flex-1">
                                        <img src={card.image} alt={card.imageAlt} className="w-fit h-auto mb-4 rounded-lg" />
                                        <h3 className="text-2xl font-nunito font-bold text-black mb-4">
                                            {card.title}
                                        </h3>
                                        <p className="text-base text-gray-600 leading-relaxed font-nunito mb-6">
                                            {card.description}
                                        </p>
                                    </div>
                                    <Link to={card.linkTo} className="font-nunito bg-transparent border-none text-blue-500 underline cursor-pointer py-2 px-0 text-sm hover:text-blue-600 disabled:opacity-50 transition-colors duration-200 mt-auto">
                                        Learn More <FaArrowRight className="inline-block ml-1" />
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            {/* Main Solutions Card */}
            <div 
                ref={solutionsRef}
                id="solutions" 
                className={`bg-white rounded-[20px] p-12 lg:p-16 shadow-sm transition-all duration-1000 ease-out transform ${
                    isSolutionsVisible 
                        ? 'opacity-100 translate-y-0' 
                        : 'opacity-0 translate-y-8'
                }`}
            >
                <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-12">
                    <div className="flex-1 space-y-6">
                        <h1 className="text-4xl lg:text-5xl font-nunito font-bold text-black leading-tight">
                            Our solutions
                        </h1>
                        <p className="text-lg text-gray-500 leading-relaxed font-nunito">
                            AmpTrans is committed to connecting the EV industry, developing solutions for optimal cost efficency and convenience for our partners. 
                        </p>
                        <p className="text-lg text-gray-500 leading-relaxed font-nunito">
                            We pledge to provide specific solutions for specific problems, and aim to help our partners succeed in their goals.
                        </p>
                        <div className="pt-4">
                            <Link 
                                to="/solutions" 
                                className="btn-primary btn-lg"
                            >
                                Get started
                            </Link>
                        </div>
                    </div>
                    <div className="flex-1">
                        <img 
                            className="w-full h-auto rounded-lg shadow-md" 
                            src='img/homepage/solar.png' 
                            alt="trucks" 
                        />
                    </div>
                </div>
            </div>

            {/* Calculator Card */}
            <div 
                ref={calculatorRef}
                className={`bg-white rounded-[20px] p-12 lg:p-16 shadow-sm transition-all duration-1000 ease-out transform ${
                    isCalculatorVisible 
                        ? 'opacity-100 translate-y-0' 
                        : 'opacity-0 translate-y-8'
                }`}
            >
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-3xl lg:text-4xl font-nunito font-bold text-black mb-8">
                        Find the tool that best fits your needs today
                    </h1>
                    <div>
                        <Link 
                            to="/solutions" 
                            className="btn-primary btn-lg"
                        >
                            Explore your options
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    )
}